import { addCubaCalendarDays } from '../time/cubaTime';
import { normalizePlayNumber, type NumberCapConfig } from './numberCaps';
import {
  clampPayoutMultiplier,
  payoutMultiplierForType,
  type PayoutMultipliers,
} from './payoutMultipliers';

export type ConstraintWindow = 'day' | 'night';

export type ConstraintSchedule =
  | { kind: 'draws'; startDayKey: string; startWindow: ConstraintWindow; count: number }
  | { kind: 'days'; startDayKey: string; count: number }
  | { kind: 'range'; startDayKey: string; endDayKey: string }
  | { kind: 'season'; startMd: string; endMd: string };

export type NumberConstraint = {
  id: string;
  numbers: string[];
  playTypes?: string[];
  cap?: number;
  multipliers?: Partial<Record<string, number>>;
  schedule: ConstraintSchedule;
  enabled: boolean;
  note?: string;
};

export type ConstraintTurn = { dayKey: string; window: ConstraintWindow };

function shiftCubaDayKey(dayKey: string, deltaDays: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey ?? '').trim());
  if (!match) return dayKey;
  const next = addCubaCalendarDays(
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    deltaDays,
  );
  return `${next.year}-${String(next.month).padStart(2, '0')}-${String(next.day).padStart(2, '0')}`;
}

function nextTurn(turn: ConstraintTurn): ConstraintTurn {
  if (turn.window === 'day') return { dayKey: turn.dayKey, window: 'night' };
  return { dayKey: shiftCubaDayKey(turn.dayKey, 1), window: 'day' };
}

function turnsEqual(a: ConstraintTurn, b: ConstraintTurn): boolean {
  return a.dayKey === b.dayKey && a.window === b.window;
}

function monthDayValue(md: string): number | null {
  const match = /^(\d{2})-(\d{2})$/.exec(String(md ?? '').trim());
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return month * 100 + day;
}

function dayKeyMonthDay(dayKey: string): number | null {
  const match = /^\d{4}-(\d{2})-(\d{2})$/.exec(String(dayKey ?? '').trim());
  if (!match) return null;
  return Number(match[1]) * 100 + Number(match[2]);
}

export function isSeasonActive(startMd: string, endMd: string, dayKey: string): boolean {
  const start = monthDayValue(startMd);
  const end = monthDayValue(endMd);
  const cur = dayKeyMonthDay(dayKey);
  if (start == null || end == null || cur == null) return false;
  if (start <= end) return cur >= start && cur <= end;
  return cur >= start || cur <= end;
}

function isWithinDraws(schedule: Extract<ConstraintSchedule, { kind: 'draws' }>, turn: ConstraintTurn): boolean {
  const count = Math.floor(Number(schedule.count));
  if (!(count > 0) || count > 366) return false;
  let cursor: ConstraintTurn = {
    dayKey: schedule.startDayKey,
    window: schedule.startWindow === 'night' ? 'night' : 'day',
  };
  for (let i = 0; i < count; i += 1) {
    if (turnsEqual(cursor, turn)) return true;
    cursor = nextTurn(cursor);
  }
  return false;
}

function isWithinDays(startDayKey: string, count: number, dayKey: string): boolean {
  const n = Math.floor(Number(count));
  if (!(n > 0) || n > 366) return false;
  let cursor = startDayKey;
  for (let i = 0; i < n; i += 1) {
    if (cursor === dayKey) return true;
    cursor = shiftCubaDayKey(cursor, 1);
  }
  return false;
}

export function isConstraintActive(constraint: NumberConstraint, turn: ConstraintTurn): boolean {
  if (!constraint.enabled) return false;
  const schedule = constraint.schedule;
  if (schedule.kind === 'draws') return isWithinDraws(schedule, turn);
  if (schedule.kind === 'days') return isWithinDays(schedule.startDayKey, schedule.count, turn.dayKey);
  if (schedule.kind === 'range') {
    return turn.dayKey >= schedule.startDayKey && turn.dayKey <= schedule.endDayKey;
  }
  if (schedule.kind === 'season') return isSeasonActive(schedule.startMd, schedule.endMd, turn.dayKey);
  return false;
}

function constraintTouches(
  constraint: NumberConstraint,
  playType: string,
  numbers: string[],
): boolean {
  if (constraint.playTypes?.length && !constraint.playTypes.includes(playType)) return false;
  const keys = numbers.map((n) => normalizePlayNumber(n, playType));
  return constraint.numbers.some((raw) => {
    const key = normalizePlayNumber(raw, playType);
    return keys.includes(key) || numbers.includes(raw);
  });
}

export function resolveEffectivePayoutMultiplier(
  playType: string,
  house: PayoutMultipliers,
  constraints: NumberConstraint[],
  turn: ConstraintTurn,
  numbers: string[],
): number {
  let multiplier = payoutMultiplierForType(house, playType);
  for (const constraint of constraints) {
    if (!isConstraintActive(constraint, turn)) continue;
    if (!constraintTouches(constraint, playType, numbers)) continue;
    const extra = clampPayoutMultiplier(constraint.multipliers?.[playType]);
    if (extra != null) multiplier = Math.min(multiplier, extra);
  }
  return multiplier;
}

export function applyConstraintCaps(
  config: NumberCapConfig,
  constraints: NumberConstraint[],
  turn: ConstraintTurn,
  playType: string,
): NumberCapConfig {
  const numberCaps = { ...(config.numberCaps ?? {}) };
  for (const constraint of constraints) {
    if (!isConstraintActive(constraint, turn)) continue;
    if (constraint.playTypes?.length && !constraint.playTypes.includes(playType)) continue;
    if (constraint.cap == null) continue;
    const cap = Math.max(0, Math.floor(Number(constraint.cap)));
    for (const raw of constraint.numbers) {
      const key = normalizePlayNumber(raw, playType);
      const current = numberCaps[key] ?? config.perNumberCap;
      numberCaps[key] = Math.min(current, cap);
    }
  }
  return { ...config, numberCaps };
}

export function parseConstraintSchedule(raw: unknown): ConstraintSchedule | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const kind = String(row.kind ?? '');
  if (kind === 'draws') {
    const count = Math.floor(Number(row.count));
    const startDayKey = String(row.startDayKey ?? '').trim();
    const startWindow = row.startWindow === 'night' ? 'night' : 'day';
    if (!startDayKey || !(count > 0)) return null;
    return { kind: 'draws', startDayKey, startWindow, count };
  }
  if (kind === 'days') {
    const count = Math.floor(Number(row.count));
    const startDayKey = String(row.startDayKey ?? '').trim();
    if (!startDayKey || !(count > 0)) return null;
    return { kind: 'days', startDayKey, count };
  }
  if (kind === 'range') {
    const startDayKey = String(row.startDayKey ?? '').trim();
    const endDayKey = String(row.endDayKey ?? '').trim();
    if (!startDayKey || !endDayKey || startDayKey > endDayKey) return null;
    return { kind: 'range', startDayKey, endDayKey };
  }
  if (kind === 'season') {
    const startMd = String(row.startMd ?? '').trim();
    const endMd = String(row.endMd ?? '').trim();
    if (monthDayValue(startMd) == null || monthDayValue(endMd) == null) return null;
    return { kind: 'season', startMd, endMd };
  }
  return null;
}

export function parseNumberConstraint(raw: unknown, fallbackId = ''): NumberConstraint | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const schedule = parseConstraintSchedule(row.schedule);
  const numbers = Array.isArray(row.numbers)
    ? row.numbers.map((n) => String(n).trim()).filter(Boolean)
    : [];
  if (!schedule || numbers.length === 0) return null;
  const playTypes = Array.isArray(row.playTypes)
    ? row.playTypes.map((t) => String(t).toLowerCase()).filter(Boolean)
    : undefined;
  const multipliersRaw = row.multipliers && typeof row.multipliers === 'object'
    ? row.multipliers as Record<string, unknown>
    : undefined;
  const multipliers: Partial<Record<string, number>> = {};
  if (multipliersRaw) {
    for (const [key, value] of Object.entries(multipliersRaw)) {
      const clamped = clampPayoutMultiplier(value);
      if (clamped != null) multipliers[key] = clamped;
    }
  }
  const cap = row.cap == null ? undefined : Math.max(0, Math.floor(Number(row.cap)));
  return {
    id: String(row.id ?? fallbackId),
    numbers,
    playTypes: playTypes?.length ? playTypes : undefined,
    cap: cap != null && Number.isFinite(cap) ? cap : undefined,
    multipliers: Object.keys(multipliers).length ? multipliers : undefined,
    schedule,
    enabled: row.enabled !== false,
    note: typeof row.note === 'string' && row.note.trim() ? row.note.trim().slice(0, 80) : undefined,
  };
}
